// Replacement and Sync Functions for Table Editor

import { matchOnlySync, matchAllSync } from './data-sync.js';

export function handleMatchSelected(sourceData2D: any[][], targetData2D: any[][], selectedRows: Set<number>) {
  if (selectedRows.size === 0) {
    alert('Please select rows to replace');
    return targetData2D;
  }
  
  // Filter source data to only include selected rows
  const filteredSourceData = filterRowsBySelection(sourceData2D, selectedRows);
  
  if (filteredSourceData.length <= 1) {
    alert('No valid source rows selected');
    return targetData2D;
  }
  
  // Use match-only sync logic but only with selected rows
  const grouperColumns = ['Group']; // Default grouper columns
  const updatedTarget = matchOnlySync(filteredSourceData, targetData2D, grouperColumns);
  
  return updatedTarget;
}

/**
 * Replace selected rows in the study table using selected rows from the document table.
 * 
 * This function allows a user to select specific rows from the document's 2D data (documentData2D)
 * and replace/sync only those rows (based on the 'Group' column) in the target study table (studyData2D).
 * 
 * Behavior:
 * - If no rows are selected, the user is alerted and the original study data is returned.
 * - The header row is always retained. Only user-selected rows (from documentData2D) are synced.
 * - Uses matchOnlySync to update only the rows that match the 'Group' columns between document and study data.
 * - If selection is invalid (no data rows after filtering), alerts the user and returns original data.
 * 
 * @param documentData2D - 2D array representing the document table (header row + data rows).
 * @param studyData2D - 2D array representing the study table (header row + data rows).
 * @param selectedRows - Set of indices (relative to data, not header) indicating which document rows to use.
 * @returns Updated 2D study table array with selected rows replaced; or original if no changes made.
 */
export function handleDocumentStudySelected(
  documentData2D: any[][], 
  studyData2D: any[][], 
  selectedRows: Set<number>
) {
  if (selectedRows.size === 0) {
    alert('Please select rows to replace');
    return studyData2D;
  }
  
  // Filter document data to only include selected rows
  const filteredDocumentData = filterRowsBySelection(documentData2D, selectedRows);
  
  if (filteredDocumentData.length <= 1) {
    alert('No valid document rows selected');
    return studyData2D;
  }
  
  // Use match-only sync logic but only with selected rows
  const grouperColumns = ['Group']; // Default grouper columns
  const updatedStudy = matchOnlySync(filteredDocumentData, studyData2D, grouperColumns);
  
  return updatedStudy;
}

export function handleDocumentStudyMatching(documentData2D: any[][], studyData2D: any[][]) {
  
  // Use match-only sync logic to update matching rows based on grouper columns
  const grouperColumns = ['Group']; // Default grouper columns
  const updatedStudy = matchOnlySync(documentData2D, studyData2D, grouperColumns);
  
  return updatedStudy;
}

export function handleDocumentStudyAll(documentData2D: any[][], studyData2D: any[][]) {
  
  // Use match-all sync logic to replace entire study table with document data
  const updatedStudy = matchAllSync(documentData2D, studyData2D);
  
  return updatedStudy;
}

export function getSelectedRowsFromGrid(gridApi: any): Set<number> {
  if (!gridApi) return new Set();
  
  const selectedNodes = gridApi.getSelectedNodes();
  const selectedRows = new Set<number>();
  
  selectedNodes.forEach((node: any) => {
    if (node.rowIndex !== null && node.rowIndex !== undefined) {
      selectedRows.add(node.rowIndex);
    }
  });
  
  return selectedRows;
}

/**
 * Filter 2D array to only include selected rows (plus header)
 * @param data2D - 2D array with header in first row
 * @param selectedRows - Set of row indices to include
 * @returns Filtered 2D array
 */
function filterRowsBySelection(data2D: any[][], selectedRows: Set<number>): any[][] {
  if (!data2D || data2D.length === 0) return [];
  
  const result = [data2D[0]]; // Always include header
  
  selectedRows.forEach(rowIndex => {
    const actualRowIndex = rowIndex + 1; // Account for header row
    if (actualRowIndex < data2D.length) {
      result.push(data2D[actualRowIndex]);
    }
  });
  
  return result;
}
